import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore, hasFunction } from '../../stores/authStore';
import './Layout.css';

interface MenuChild {
  label: string;
  path: string;
  requiredFunction?: string;
}

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiredFunction?: string;
  children?: MenuChild[];
}

const menuItems: MenuItem[] = [
  {
    label: 'Home',
    path: '/home',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect width="7" height="9" x="3" y="3" rx="1" />
        <rect width="7" height="5" x="14" y="3" rx="1" />
        <rect width="7" height="9" x="14" y="12" rx="1" />
        <rect width="7" height="5" x="3" y="16" rx="1" />
      </svg>
    ),
    // Home 不需要权限控制
  },
  {
    label: 'Overview',
    path: '/overview',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18" />
        <path d="M18 17V9" />
        <path d="M13 17V5" />
        <path d="M8 17v-3" />
      </svg>
    ),
    children: [
      {
        label: 'Token Report',
        path: '/overview/token-report',
        requiredFunction: 'token_report_view',
      },
      { label: 'Logs', path: '/overview/logs', requiredFunction: 'logs_view' },
      {
        label: 'Topup History',
        path: '/overview/topup-history',
        requiredFunction: 'organization_list_view',
      },
      {
        label: 'Payment Orders',
        path: '/overview/payment-orders',
        requiredFunction: 'organization_list_view',
      },
    ],
  },
  {
    label: 'Content',
    path: '/content',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 5h16" />
        <path d="M4 12h16" />
        <path d="M4 19h10" />
      </svg>
    ),
    children: [
      { label: 'Blog', path: '/content/blog-studio' },
      { label: 'Careers', path: '/content/careers-studio' },
    ],
  },
  {
    label: 'Organizations',
    path: '/organizations',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4 8 4v14" />
        <path d="M9 21v-6h6v6" />
      </svg>
    ),
    children: [
      {
        label: 'Organization List',
        path: '/organizations/list',
        requiredFunction: 'organization_list_view',
      },
      {
        label: 'Guardrail List',
        path: '/organizations/guardrails',
        requiredFunction: 'guardrail_list_view',
      },
    ],
  },
  {
    label: 'Projects',
    path: '/projects',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2H2v10h10V2z" />
        <path d="M22 2h-10v10h10V2z" />
        <path d="M12 12H2v10h10V12z" />
        <path d="M22 12h-10v10h10V12z" />
      </svg>
    ),
    children: [
      { label: 'Project List', path: '/projects/list', requiredFunction: 'project_list_view' },
      { label: 'API Key', path: '/projects/api-keys', requiredFunction: 'api_key_list_view' },
    ],
  },
  {
    label: 'Users',
    path: '/users',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    children: [{ label: 'User List', path: '/users/list', requiredFunction: 'user_list_view' }],
  },
  {
    label: 'Models',
    path: '/models',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        <path d="M5 3v4" />
        <path d="M19 17v4" />
        <path d="M3 5h4" />
        <path d="M17 19h4" />
      </svg>
    ),
    children: [
      { label: 'Provider List', path: '/models/providers', requiredFunction: 'provider_list_view' },
      { label: 'Model List', path: '/models/list', requiredFunction: 'model_list_view' },
      { label: 'Accounts', path: '/models/accounts', requiredFunction: 'account_list_view' },
      {
        label: 'Price Center',
        path: '/models/price-center',
        requiredFunction: 'price_center_view',
      },
    ],
  },
  {
    label: 'Admin Settings',
    path: '/admin',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <path d="M12 2v20" />
        <path d="M2 12h20" />
      </svg>
    ),
    children: [
      { label: 'Function List', path: '/admin/functions', requiredFunction: 'function_list_view' },
      { label: 'Role List', path: '/admin/roles', requiredFunction: 'role_list_view' },
      { label: 'Employee List', path: '/admin/employees', requiredFunction: 'employee_list_view' },
    ],
  },
  {
    label: 'Account Settings',
    path: '/settings',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l-.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    // Account Settings 不需要权限控制（修改密码页对所有用户开放）
    children: [{ label: 'Change Password', path: '/change-password' }],
  },
];

interface SidebarProps {
  isMobileNavOpen?: boolean;
  onClose?: () => void;
  onNavigate?: () => void;
}

export default function Sidebar({
  isMobileNavOpen = false,
  onClose,
  onNavigate,
}: SidebarProps = {}) {
  const location = useLocation();
  const { user } = useAuthStore();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    '/overview': true, // expand Overview by default
    '/content': true, // expand Content by default
    '/organizations': true, // expand Organizations by default
    '/projects': true, // expand Projects by default
    '/users': true, // expand Users by default
    '/models': true, // expand Models by default
    '/admin': true, // expand Admin Settings by default
    '/settings': true, // expand Account Settings by default
  });

  // Check if primary menu item is active (exact match only)
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleExpand = (path: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  // Filter menu items based on user permissions
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .map(item => {
        // Check if parent menu requires permission
        if (item.requiredFunction && !hasFunction(user, item.requiredFunction)) {
          return null;
        }

        // Filter children based on permissions
        if (item.children) {
          const visibleChildren = item.children.filter(
            child => !child.requiredFunction || hasFunction(user, child.requiredFunction)
          );

          // If no children are visible, don't show the parent menu
          if (visibleChildren.length === 0) {
            return null;
          }

          return { ...item, children: visibleChildren };
        }

        return item;
      })
      .filter((item): item is MenuItem => item !== null);
  };

  const visibleMenuItems = filterMenuItems(menuItems);

  return (
    <aside
      id="admin-mobile-sidebar"
      className={`layout-sidebar ${isMobileNavOpen ? 'mobile-open' : ''}`}
    >
      {/* 移动端侧边栏头部 */}
      <div className="sidebar-mobile-header">
        <div>
          <p className="sidebar-mobile-label">Navigation</p>
          <p className="sidebar-mobile-title">Admin menu</p>
        </div>
        <button
          type="button"
          className="sidebar-mobile-close-btn"
          aria-label="关闭导航菜单"
          onClick={() => onClose?.()}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Main navigation menu */}
      <div className="sidebar-section">
        <nav className="sidebar-nav">
          {visibleMenuItems.map(item => (
            <div key={item.path} className="nav-item-wrapper">
              {item.children ? (
                // Items with children
                <div className="nav-group">
                  <button className="sidebar-nav-item" onClick={() => toggleExpand(item.path)}>
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`nav-arrow ${expandedItems[item.path] ? 'open' : ''}`}
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {expandedItems[item.path] && (
                    <div className="nav-sub-items">
                      {item.children.map(child => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => onNavigate?.()}
                          className={`nav-sub-item ${location.pathname === child.path ? 'active' : ''}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Items without children
                <Link
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}
